<template>
  <template v-if="type === 'icon'">
    <img v-if="utils.isValidStringValue(resource.ResourceIcon)" :src="resource.ResourceIcon">
    <font-awesome-icon v-else :icon="faIcon" class="w-5 h-5" />
    <tx-actions-button v-if="visibleActions.length > 0" :items="getActions" />
  </template>
  <div
    v-else-if="type === 'tile'"
    class="flex items-center space-x-2 p-2 shadow border rounded-lg"
  >
    <img
      v-if="faIcon === 'fa-light fa-image'"
      :src="utils.isValidStringValue(resource.ResourceIcon) ? resource.ResourceIcon : resource.ResourceUrl"
      :alt="resource.ResourceName"
      class="w-12 h-12 rounded-full object-cover"
    >
    <font-awesome-icon v-else :icon="faIcon" class="w-10 h-10" />
    <div>
      <h3 class="text-lg font-semibold">
        {{ resource.ResourceName }}
      </h3>
      <p class="text-sm text-gray-600">
        {{ resource.ResourceDescription }}
      </p>
    </div>
    <tx-actions-button v-if="visibleActions.length > 0" :items="getActions" @click.stop />
  </div>
  <div
    v-else-if="type === 'card'"
    class="flex flex-col items-center p-4 shadow-card rounded-lg hover:shadow-lg transition-all duration-200"
  >
    <img
      v-if="faIcon === 'fa-light fa-image'"
      :src="utils.isValidStringValue(resource.ResourceIcon) ? resource.ResourceIcon : resource.ResourceUrl"
      :alt="resource.ResourceName"
      class="w-24 h-24 object-scale-down"
    >
    <font-awesome-icon v-else :icon="faIcon" class="w-20 h-20" />
    <div class="flex items-center w-full mt-2 space-x-2 text-center">
      <h3
        :class="[visibleActions.length > 0 ? 'w-3/4' : 'w-full']"
        class="text-md font-semibold truncate"
        :title="resource.ResourceName"
      >
        {{ resource.ResourceName }}
      </h3>

      <div
        v-if="visibleActions.length > 0"
        class="w-1/4 flex justify-end z-10"
        @click.stop
      >
        <tx-actions-button :items="visibleActions" @click.stop />
      </div>
    </div>
    <p class="w-full text-sm text-center text-gray-600 line-clamp-1">
      {{ resource.ResourceDescription }}
    </p>
  </div>
  <div
    v-else
    class="flex items-center space-x-2 p-2 border rounded-lg"
  >
    <h3 class="text-lg font-semibold">
      {{ resource.ResourceName }}
    </h3>
    <p class="text-sm text-gray-600">
      {{ resource.ResourceDescription }}
    </p>
    <tx-actions-button v-if="visibleActions.length > 0" :items="getActions" @click.stop />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type Resource from '@/models/resource'
import utils from '@/services/utils'
import { privileges } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import TxActionsButton from '@/shared/components/TxActionsButton.vue'

interface IProps {
  type?: 'normal' | 'tile' | 'card' | 'icon'
  resource: Resource
  actions?: string[]
}
const props = withDefaults(defineProps<IProps>(), {
  type: 'normal',
})

const emit = defineEmits<{
  (e: 'actionClick', resource: Resource, action: string): void
}>()

const userStore = useUserStore()

const fileExtension = computed(() => props.resource.ResourceUrl.split('.').pop())

const faIcon = computed(() => {
  switch (fileExtension.value) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'tif':
    case 'bmp':
      return 'fa-light fa-image'
    case 'mp4':
      return 'fa-light fa-video'
    case 'pdf':
      return 'fa-light fa-file-pdf'
    case 'xls':
    case 'xlsx':
    case 'xlsb':
      return 'fa-light fa-file-excel'
    case 'doc':
    case 'docx':
    case 'rtf':
      return 'fa-light fa-file-word'
    case 'ppt':
    case 'pptx':
      return 'fa-light fa-file-powerpoint'
    case 'txt':
      return 'fa-light fa-file-text'
    case 'zip':
    case 'rar':
    case '7z':
      return 'fa-light fa-file-zipper'
    default:
      return 'fa-light fa-file'
  }
})

const getActions = computed(() => {
  if (props.actions) {
    return props.actions.map((action) => {
      return {
        key: getActionKey(action),
        label: action,
        icon: getActionIcon(action),
        disabled: false,
        visible: hideAndShowActions(action),
        onClick: () => onActionClick(props.resource, action),
      } as ITxActionItem
    })
  }
  else {
    return []
  }
})

const visibleActions = computed(() => getActions.value.filter(action => action.visible))

function getActionKey(action: string) {
  switch (action) {
    case 'Delete':
      return 'delete'
  }
}

function getActionIcon(action: string) {
  switch (action) {
    case 'Delete':
      return 'fa-light fa-trash-can'
  }
  return ''
}

function hideAndShowActions(action: string) {
  switch (action) {
    case 'Delete':
      return userStore.userProfile.isValidPrivilege(privileges.resource.updateStatus) && props.resource.CatalogCode === userStore.activeCatalog?.CatalogCode
  }
  return true
}

function onActionClick(resource: Resource, action: string) {
  emit('actionClick', resource, action)
}
</script>
